import clsx from 'clsx';
import {motion, useTransform, useScroll} from 'framer-motion';
import {Link as ScrollLink} from 'react-scroll';
import {useState, useRef, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';

import {pathVars} from '../ui/animationVars';
import {useSiteContext} from '../../hooks/useSiteContext';

type Props = {
  image: any;
  title: string;
  rollingText: string[];
  anchorLinks: [
    {
      title: string;
      slug: {
        current: string;
      };
    },
  ];
};

export default function Banner({
  image,
  title,
  anchorLinks,
  rollingText,
}: Props) {
  const {state} = useSiteContext();

  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: false,
  });

  const {scrollY} = useScroll();

  const y = useTransform(
    scrollY,
    [0, 2000], // px to scroll
    [0, 1000], // y change
  );

  //handle sticky subnav
  const navRef = useRef<any>(null);
  const bannerRef = useRef<any>(null);

  const [navActive, setNavActive] = useState<boolean>(false);

  //detect when navRef is at the top of the page
  useEffect(() => {
    const handleScroll = () => {
      if (navRef?.current?.getBoundingClientRect()?.top <= 0) {
        setNavActive(true);
      } else {
        setNavActive(false);
      }
    };
    const handleScroll2 = () => {
      if (bannerRef.current.getBoundingClientRect().bottom >= 0) {
        setNavActive(false);
      } else {
        setNavActive(true);
      }
    };
    window.addEventListener('scroll', handleScroll, {passive: true});
    window.addEventListener('scroll', handleScroll2, {passive: true});
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll2);
    };
  }, [navRef]);

  //get height of navRef
  const [navHeight, setNavHeight] = useState<number>(0);
  useEffect(() => {
    setNavHeight(
      navRef.current ? navRef.current.getBoundingClientRect().height : 0,
    );
  }, [navRef]);

  function scrollToValue(value: string) {
    const element = document.getElementById(value);
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }

  //rolling text state
  const [activeWord, setActiveWord] = useState<number>(0);

  //change active word every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeWord < rollingText.length - 1) {
        setActiveWord(activeWord + 1);
      } else {
        setActiveWord(0);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeWord]);

  return (
    <div
      ref={bannerRef}
      style={{marginBottom: `${navHeight}px`}}
      className="relative flex items-center justify-center bg-light-beige"
    >
      <motion.img
        style={{y: y}}
        src={image?.url}
        alt={title}
        className="hidden h-full w-full md:block"
      />
      <div className="aspect-w-4 aspect-h-5 relative h-full w-full md:hidden">
        <motion.img
          style={{y: y}}
          src={image?.url}
          alt={title}
          className="absolute top-0 left-0 h-full w-full object-cover"
        />
      </div>
      <div className="absolute top-space-l text-center">
        <h1
          ref={ref}
          className="relative z-10 text-[38px] font-bold text-white md:text-[64px] xl:text-[80px]"
        >
          {title}
          <br />
          <div className="relative inline-block w-max">
            <span className="opacity-0">{rollingText[0]}</span>
            {rollingText.map((word, i) => (
              <motion.span
                key={i}
                initial={{opacity: 0}}
                animate={
                  i === activeWord
                    ? {
                        opacity: 1,
                        transition: {
                          delay: 0.5,
                          duration: 0.3,
                          ease: 'easeInOut',
                        },
                      }
                    : {opacity: 0}
                }
                className="absolute top-0 left-0 w-full text-center"
              >
                {word}
              </motion.span>
            ))}
            <svg
              className="absolute -bottom-2 w-full"
              width="404"
              height="17"
              viewBox="0 0 404 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <motion.path
                variants={pathVars}
                initial="initial"
                animate={inView ? 'animate' : 'initial'}
                d="M3 8.86221C134.799 3.71797 270.636 -3.29995 401 14"
                stroke="#F27021"
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </h1>
      </div>

      {/* Subnav */}
      <div
        ref={navRef}
        className={clsx([
          'z-20 w-full',
          navActive ? 'fixed top-0' : 'absolute top-full',
        ])}
      >
        <div className="jusity-between flex w-full gap-x-8 bg-light-beige py-3 px-gutter">
          {anchorLinks && (
            <div className="hidden xl:block">
              <span className="blockH6 block whitespace-nowrap font-bold text-orange">
                Our People
              </span>
            </div>
          )}
          {/* Mobile Links */}
          <div className="richH5 relative flex w-full translate-y-1 items-center justify-center font-bold xl:hidden">
            <select
              value={state.activeModule || 'select'}
              onChange={(e) => scrollToValue(e.target.value)}
              style={{background: 'none'}}
              className="w-full border-0 bg-opacity-0 px-0 focus:border-0 focus:ring-0"
            >
              <option disabled value="select">
                Select
              </option>
              {anchorLinks?.map(({title, slug}: any, i: number) => (
                <option key={i} value={slug?.current}>
                  {title}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-0">+</span>
          </div>
          {/* Desktop Links */}
          <div className="hidden w-full items-center justify-end gap-x-10 xl:flex">
            {anchorLinks?.map(({slug, title}: any, i: number) => (
              <ScrollLink
                key={i}
                className={clsx([
                  'blockH8 transition-standard cursor-pointer hover:text-orange',
                  slug?.current === state?.activeModule
                    ? 'text-orange'
                    : 'text-dark-grey',
                ])}
                to={slug?.current}
                offset={0}
                smooth={true}
              >
                {title}
              </ScrollLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
